import { Component, useEffect, useMemo, useRef, useState, useCallback, type ReactNode } from 'react';
import { io, type Socket } from 'socket.io-client';
import {
  MessageSquare, RefreshCcw, Plus, Trash2, Search, Send, CircleCheck,
  ExternalLink, Save, X, CircleAlert, ShieldCheck, Radio,
} from 'lucide-react';
import {
  The, DauThe, TheSoLieu, NutPhu, NutChinh, ChuaCoDuLieu, DangTai, AnhDaiDien, NhanTrangThai,
} from '../components/ui';
import { api, LoiMayChu } from '../services/api';
import type { BaiDeBinhLuan, BoCauBinhLuan, TaiKhoanFB, TrangThaiJob } from '../services/kieu';
import { avatarActor, tenActor } from '../services/taiKhoan';

/**
 * Màn Bình luận — tạo job bình luận vào bài viết đã đăng thành công.
 *
 * Điểm an toàn quan trọng: target gửi xuống backend LUÔN là postId, không dùng
 * groupId. Backend đã từng bắt được ca dán nhầm ID nhóm vào ô ID bài viết; màn
 * này ưu tiên chọn bài từ /postable để tránh lỗi đó ngay từ giao diện.
 */

const KHO_NHAP = 'fbmax_binh_luan_nhap';
const GIAN_CACH_TOI_THIEU = 10;
const GIAN_CACH_NE_SPAM_TOI_THIEU = 30;
const GIAN_CACH_TOI_DA = 3600;
const TRANG_THAI_CUOI = new Set(['SUCCESS', 'FAILED', 'SKIPPED', 'UNKNOWN_RESULT', 'ABORTED', 'CANCELLED', 'DELETED']);

interface CapNhatJobSocket {
  jobId: string;
  status: TrangThaiJob;
  detail: string | null;
  updatedAt: string;
}

interface JobBinhLuanTheoDoi {
  jobId: string;
  status: TrangThaiJob | 'QUEUED';
  detail: string | null;
  updatedAt: string;
  evidenceUrl?: string | null;
}

class LoiVungBinhLuan extends Component<{ children: ReactNode }, { loi: Error | null }> {
  state = { loi: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { loi: error };
  }

  componentDidCatch(error: Error) {
    console.error('[BinhLuan] render error', error);
  }

  render() {
    if (this.state.loi) {
      return (
        <div className="mx-auto max-w-[1440px] rounded-xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-800 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300">
          <p className="font-semibold">Vùng Bình luận đang gặp lỗi hiển thị.</p>
          <p className="mt-1">Các vùng khác của dashboard vẫn hoạt động. Hãy bấm tải lại vùng này hoặc chuyển màn rồi quay lại.</p>
          <button
            type="button"
            onClick={() => this.setState({ loi: null })}
            className="mt-3 rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100 dark:border-rose-500/30 dark:text-rose-300 dark:hover:bg-rose-500/10"
          >
            Tải lại vùng Bình luận
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function khoaNhap(actorUid?: string | null): string {
  return `${KHO_NHAP}:${actorUid || 'chua-co-actor'}`;
}

function docNhapCu(actorUid?: string | null): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(khoaNhap(actorUid)) || 'null');
    return Array.isArray(v) && v.length ? v.map(String) : [''];
  } catch {
    return [''];
  }
}

function taoOperationId(actorUid: string) {
  const rnd = crypto.getRandomValues(new Uint32Array(2));
  return `comment_${actorUid}_${rnd[0].toString(36)}${rnd[1].toString(36)}`;
}

function gioNgan(v?: string | number | null) {
  if (!v) return '—';
  const t = typeof v === 'number' ? v : Date.parse(v);
  if (!Number.isFinite(t)) return '—';
  const d = new Date(t);
  return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' })
    + ' ' + d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
}

function layPostId(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  const m = s.match(/posts\/(\d+)/) || s.match(/[?&]story_fbid=(\d+)/)
    || s.match(/[?&]fbid=(\d+)/) || s.match(/permalink\/(\d+)/)
    || s.match(/^(\d{5,})$/);
  return m ? m[1] : null;
}

function nhanTrangThaiJob(status: JobBinhLuanTheoDoi['status']) {
  if (status === 'QUEUED') return { chu: 'Đã vào hàng đợi', mau: 'xam' as const };
  if (status === 'PENDING') return { chu: 'Đang chờ', mau: 'xam' as const };
  if (status === 'PAUSED') return { chu: 'Tạm dừng', mau: 'vang' as const };
  if (status === 'SUCCESS') return { chu: 'Đã xác nhận xong', mau: 'xanh' as const };
  if (status === 'FAILED') return { chu: 'Thất bại', mau: 'do' as const };
  if (status === 'UNKNOWN_RESULT') return { chu: 'Chưa xác định', mau: 'vang' as const };
  if (status === 'SKIPPED') return { chu: 'Đã bỏ qua', mau: 'xam' as const };
  if (status === 'ABORTED' || status === 'CANCELLED' || status === 'DELETED') return { chu: 'Đã dừng', mau: 'xam' as const };
  return { chu: status, mau: 'xam' as const };
}

export function ManBinhLuan(props: { onLamMoi: () => void; taiKhoan: TaiKhoanFB | null; token: string | null }) {
  return (
    <LoiVungBinhLuan>
      <ManBinhLuanNoiDung {...props} />
    </LoiVungBinhLuan>
  );
}

function ManBinhLuanNoiDung({ onLamMoi, taiKhoan, token }: { onLamMoi: () => void; taiKhoan: TaiKhoanFB | null; token: string | null }) {
  const [bai, setBai] = useState<BaiDeBinhLuan[]>([]);
  const [boCau, setBoCau] = useState<BoCauBinhLuan[]>([]);
  const [dangTai, setDangTai] = useState(true);
  const [loi, setLoi] = useState<string | null>(null);
  const [thongBao, setThongBao] = useState<{ loai: 'hang-doi' | 'luu'; chu: string } | null>(null);

  const [cau, setCau] = useState<string[]>(() => docNhapCu(taiKhoan?.uid));
  const [daChon, setDaChon] = useState<Set<string>>(new Set());
  const [tim, setTim] = useState('');
  const [nhapTay, setNhapTay] = useState('');
  const [tuGiay, setTuGiay] = useState(60);
  const [denGiay, setDenGiay] = useState(120);
  const [neSpam, setNeSpam] = useState(true);
  const [dangGui, setDangGui] = useState(false);
  const [tenBo, setTenBo] = useState('');
  const [dangLuuBo, setDangLuuBo] = useState(false);
  const [ketNoiSocket, setKetNoiSocket] = useState<'chua-ket-noi' | 'dang-ket-noi' | 'da-ket-noi' | 'loi'>('chua-ket-noi');
  const [jobTheoDoi, setJobTheoDoi] = useState<JobBinhLuanTheoDoi[]>([]);
  const dangGuiRef = useRef(false);
  const operationIdRef = useRef<string | null>(null);
  const actorNhapRef = useRef(taiKhoan?.uid || '');
  const jobTheoDoiRef = useRef<Set<string>>(new Set());

  const actorUid = taiKhoan?.uid || '';
  const actorLaPage = Boolean(taiKhoan?.isPage);
  const tenHienThi = tenActor(taiKhoan);

  useEffect(() => {
    if (actorNhapRef.current !== actorUid) return;
    localStorage.setItem(khoaNhap(actorUid), JSON.stringify(cau));
  }, [cau, actorUid]);

  useEffect(() => {
    actorNhapRef.current = actorUid;
    setCau(docNhapCu(actorUid));
    setDaChon(new Set());
    setTim('');
    setNhapTay('');
    setThongBao(null);
    setLoi(null);
    operationIdRef.current = null;
    dangGuiRef.current = false;
    setDangGui(false);
  }, [actorUid]);

  useEffect(() => {
    operationIdRef.current = null;
  }, [actorUid, cau, daChon, tuGiay, denGiay, neSpam]);

  useEffect(() => {
    jobTheoDoiRef.current = new Set(jobTheoDoi.map(j => j.jobId));
  }, [jobTheoDoi]);

  const capNhatBangChungJob = useCallback(async () => {
    const ids = jobTheoDoiRef.current;
    if (!ids.size) return;
    try {
      const kq = await api.congViec();
      const theoId = new Map((kq.jobs || []).filter(j => ids.has(j.id)).map(j => [j.id, j]));
      if (!theoId.size) return;
      setJobTheoDoi(ds => ds.map(j => {
        const moi = theoId.get(j.jobId);
        if (!moi) return j;
        return {
          ...j,
          status: moi.status,
          detail: moi.errorDetail || j.detail,
          updatedAt: moi.updatedAt || j.updatedAt,
          evidenceUrl: moi.evidenceUrl || j.evidenceUrl || null,
        };
      }));
    } catch {
      // Socket đã cập nhật trạng thái chính; snapshot chỉ là đồng bộ bù để lấy evidenceUrl nếu có.
    }
  }, []);

  useEffect(() => {
    if (!token) {
      setKetNoiSocket('chua-ket-noi');
      return;
    }
    setKetNoiSocket('dang-ket-noi');
    const socket: Socket = io('/', {
      auth: { token, client: 'dashboard-comment-screen' },
      transports: ['websocket', 'polling'],
    });

    socket.on('connect', () => setKetNoiSocket('da-ket-noi'));
    socket.on('connect_error', () => setKetNoiSocket('loi'));
    socket.on('disconnect', () => setKetNoiSocket('chua-ket-noi'));
    socket.on('JOB_STATUS_UPDATE', (data: CapNhatJobSocket) => {
      if (!data?.jobId || !jobTheoDoiRef.current.has(String(data.jobId))) return;
      setJobTheoDoi(ds => ds.map(j => j.jobId === String(data.jobId)
        ? {
            ...j,
            status: data.status,
            detail: data.detail || j.detail,
            updatedAt: data.updatedAt || new Date().toISOString(),
          }
        : j));
      if (TRANG_THAI_CUOI.has(String(data.status))) void capNhatBangChungJob();
    });

    return () => {
      socket.disconnect();
    };
  }, [token, capNhatBangChungJob]);

  const tai = useCallback(async () => {
    if (!actorUid) {
      setBai([]);
      setBoCau([]);
      setDangTai(false);
      setLoi('Chưa nhận diện được tài khoản Facebook. Mở Chrome có Extension và đăng nhập trước khi bình luận.');
      return;
    }
    setDangTai(true);
    const [dsBai, dsBo] = await Promise.allSettled([api.baiDeBinhLuan(), api.boCauBinhLuan()]);
    if (dsBai.status === 'fulfilled') setBai(dsBai.value.posts || []);
    if (dsBo.status === 'fulfilled') setBoCau(dsBo.value.collections || []);
    const hong = [dsBai, dsBo].find(x => x.status === 'rejected') as PromiseRejectedResult | undefined;
    setLoi(hong ? (hong.reason instanceof LoiMayChu ? hong.reason.message : 'Một phần dữ liệu chưa tải được.') : null);
    setDangTai(false);
  }, [actorUid]);

  useEffect(() => { tai(); }, [tai]);

  const cauHopLe = useMemo(() => cau.map(x => x.trim()).filter(Boolean), [cau]);

  const baiLoc = useMemo(() => {
    const q = tim.trim().toLowerCase();
    if (!q) return bai;
    return bai.filter(b => String(b.postId).includes(q)
      || String(b.groupId || '').includes(q)
      || String(b.message || '').toLowerCase().includes(q));
  }, [bai, tim]);

  const loiHopLe = useMemo((): string | null => {
    if (cauHopLe.length === 0) return 'Chưa có câu bình luận nào.';
    if (daChon.size === 0) return 'Chưa chọn bài viết nào.';
    if (!actorUid) return 'Chưa nhận diện tài khoản Facebook. Không thể tạo job bình luận.';
    if (tuGiay < GIAN_CACH_TOI_THIEU) return 'Khoảng nghỉ tối thiểu là 10 giây mỗi lần.';
    if (neSpam && tuGiay < GIAN_CACH_NE_SPAM_TOI_THIEU) return 'Khi bật Né spam, khoảng nghỉ tối thiểu là 30 giây.';
    if (denGiay < tuGiay) return 'Khoảng nghỉ kết thúc phải lớn hơn hoặc bằng bắt đầu.';
    if (denGiay > GIAN_CACH_TOI_DA) return 'Khoảng nghỉ tối đa là 3600 giây.';
    return null;
  }, [cauHopLe, daChon, tuGiay, denGiay, neSpam, actorUid]);

  useEffect(() => {
    if (!neSpam || tuGiay >= GIAN_CACH_NE_SPAM_TOI_THIEU) return;
    setTuGiay(GIAN_CACH_NE_SPAM_TOI_THIEU);
    setDenGiay(d => Math.max(d, GIAN_CACH_NE_SPAM_TOI_THIEU));
  }, [neSpam, tuGiay]);

  const tongJob = cauHopLe.length * daChon.size;
  const postThuCong = [...daChon].filter(id => !bai.some(b => String(b.postId) === id));

  const themTargetThuCong = () => {
    const id = layPostId(nhapTay);
    if (!id) { setLoi('Không đọc được ID bài viết. Hãy dán link bài viết hoặc dãy số ID.'); return; }
    setDaChon(s => new Set(s).add(id));
    setNhapTay('');
    setLoi(null);
  };

  const gui = async () => {
    if (dangGuiRef.current) return;
    if (loiHopLe) { setLoi(loiHopLe); return; }
    dangGuiRef.current = true;
    setDangGui(true);
    try {
      const operationId = operationIdRef.current || taoOperationId(actorUid);
      operationIdRef.current = operationId;
      const kq = await api.guiBinhLuan({
        targets: [...daChon],
        comments: cauHopLe,
        delayFromSec: tuGiay,
        delayToSec: denGiay,
        safeMode: neSpam,
        fbUid: actorUid,
      }, operationId);
      const now = new Date().toISOString();
      setJobTheoDoi((kq.jobIds || [kq.jobId]).filter(Boolean).map(jobId => ({
        jobId: String(jobId),
        status: 'QUEUED',
        detail: null,
        updatedAt: now,
        evidenceUrl: null,
      })));
      setThongBao({ loai: 'hang-doi', chu: `Đã vào hàng đợi ${kq.total} job bình luận. Kết quả thật sẽ nằm ở màn Tiến Trình khi có bằng chứng.` });
      setDaChon(new Set());
      setLoi(null);
      operationIdRef.current = null;
      onLamMoi();
    } catch (e) {
      setLoi(e instanceof LoiMayChu ? e.message : 'Không tạo được tác vụ bình luận.');
    } finally {
      dangGuiRef.current = false;
      setDangGui(false);
    }
  };

  const luuBo = async () => {
    const ten = tenBo.trim();
    if (!ten) { setLoi('Nhập tên bộ câu trước khi lưu.'); return; }
    if (!cauHopLe.length) { setLoi('Bộ câu không được rỗng.'); return; }
    setDangLuuBo(true);
    try {
      await api.luuBoCauBinhLuan({ name: ten, items: cauHopLe });
      setTenBo('');
      setThongBao({ loai: 'luu', chu: 'Đã lưu bộ câu bình luận.' });
      await tai();
    } catch (e) {
      setLoi(e instanceof LoiMayChu ? e.message : 'Không lưu được bộ câu.');
    } finally {
      setDangLuuBo(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1440px] animate-fade-in space-y-5">
      {loi && (
        <div className="flex items-start justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400">
          <span>{loi}</span>
          <button onClick={() => setLoi(null)} className="shrink-0 text-amber-600 hover:text-amber-800">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {thongBao && (
        <div className="flex items-start justify-between gap-3 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-300">
          <span>{thongBao.chu}</span>
          <button onClick={() => setThongBao(null)} className="shrink-0 text-sky-600 hover:text-sky-800">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <The className="p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <AnhDaiDien src={avatarActor(taiKhoan)} ten={tenHienThi} kichThuoc="h-12 w-12" />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="truncate text-base font-bold text-slate-900 dark:text-white">{tenHienThi}</h2>
                {taiKhoan ? <NhanTrangThai chu={actorLaPage ? 'Page' : 'Cá nhân'} mau={actorLaPage ? 'vang' : 'xanh'} /> : <NhanTrangThai chu="Chưa kết nối" mau="xam" />}
              </div>
              <p className="mt-1 font-mono text-xs text-slate-500 dark:text-slate-400">UID: {actorUid || '—'}</p>
            </div>
          </div>
          <label className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-500 opacity-60 dark:border-slate-700 dark:text-slate-400">
            <input type="checkbox" disabled checked={false} className="h-4 w-4 rounded border-slate-300" readOnly />
            Bình luận ẩn danh {actorLaPage ? 'không dùng cho Page' : 'chưa hỗ trợ ở màn này'}
          </label>
        </div>
      </The>

      <div className="grid gap-3 sm:grid-cols-3">
        <TheSoLieu icon={<MessageSquare className="h-5 w-5" />} so={cauHopLe.length} nhan="Câu bình luận" />
        <TheSoLieu icon={<CircleCheck className="h-5 w-5" />} so={daChon.size} nhan="Bài đã chọn" />
        <TheSoLieu icon={<Send className="h-5 w-5" />} so={tongJob} nhan="Tác vụ sẽ tạo" />
      </div>

      {jobTheoDoi.length > 0 && (
        <The>
          <DauThe
            icon={<Radio className="h-4 w-4" />}
            tieuDe="Theo dõi bình luận vừa gửi"
            dem={jobTheoDoi.length}
            phai={<NhanTrangThai chu={ketNoiSocket === 'da-ket-noi' ? 'Socket đang nghe' : ketNoiSocket === 'dang-ket-noi' ? 'Đang nối socket' : ketNoiSocket === 'loi' ? 'Lỗi socket' : 'Chờ socket'} mau={ketNoiSocket === 'da-ket-noi' ? 'xanh' : ketNoiSocket === 'loi' ? 'do' : 'vang'} />}
          />
          <div className="divide-y divide-slate-50 dark:divide-slate-700/50">
            {jobTheoDoi.map(j => {
              const hien = nhanTrangThaiJob(j.status);
              const chuaCoBangChung = j.status === 'SUCCESS' && !j.evidenceUrl;
              return (
                <div key={j.jobId} className="flex flex-col gap-2 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="truncate font-mono text-xs font-semibold text-slate-600 dark:text-slate-300">{j.jobId}</p>
                    <p className="mt-0.5 text-xs text-slate-400">Cập nhật {gioNgan(j.updatedAt)}</p>
                    {j.detail && <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">{j.detail}</p>}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <NhanTrangThai
                      chu={chuaCoBangChung ? 'Đã xong, đang lấy bằng chứng' : hien.chu}
                      mau={chuaCoBangChung ? 'vang' : hien.mau}
                    />
                    {j.evidenceUrl && (
                      <a href={j.evidenceUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700/50">
                        Bằng chứng <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </The>
      )}

      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600 dark:border-slate-700/60 dark:bg-slate-800/50 dark:text-slate-400">
        <p>
          Ưu tiên chọn từ <span className="font-semibold">Bài đã đăng</span> lấy qua Lab Server/SQLite, không dùng dữ liệu cũ trong localStorage.
        </p>
        <p className="mt-1">
          Bật <span className="font-semibold">Né spam</span> sẽ ép sàn giãn cách <span className="font-semibold">30 giây</span>. Sau khi gửi chỉ báo vào hàng đợi, không báo thành công khi chưa có bằng chứng.
        </p>
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
        <div className="space-y-5">
          <The>
            <DauThe icon={<MessageSquare className="h-4 w-4" />} tieuDe="Bộ câu bình luận" dem={cauHopLe.length} />
            <div className="space-y-3 p-5">
              {cau.map((dong, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    value={dong}
                    onChange={e => setCau(ds => ds.map((x, idx) => idx === i ? e.target.value : x))}
                    placeholder={`Câu bình luận ${i + 1}`}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                  />
                  <button
                    onClick={() => setCau(ds => ds.length === 1 ? [''] : ds.filter((_, idx) => idx !== i))}
                    aria-label={`Xoá câu ${i + 1}`}
                    className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-rose-600 dark:hover:bg-slate-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <NutPhu onClick={() => setCau(ds => [...ds, ''])}>
                <Plus className="h-3.5 w-3.5" />
                Thêm câu
              </NutPhu>

              <div className="grid gap-2 border-t border-slate-100 pt-4 dark:border-slate-700 sm:grid-cols-[minmax(0,1fr)_auto]">
                <input
                  value={tenBo}
                  onChange={e => setTenBo(e.target.value)}
                  placeholder="Tên bộ câu để lưu lại"
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                />
                <NutPhu onClick={luuBo} disabled={dangLuuBo}>
                  <Save className="h-3.5 w-3.5" />
                  {dangLuuBo ? 'Đang lưu...' : 'Lưu bộ câu'}
                </NutPhu>
              </div>
            </div>
          </The>

          <The>
            <DauThe icon={<Save className="h-4 w-4" />} tieuDe="Bộ câu đã lưu" dem={dangTai ? undefined : boCau.length} />
            {/* Thứ tự ba nhánh ở đây có chủ đích.
                `boCau` khởi tạo rỗng (BinhLuan.tsx:132) và chỉ được đổ dữ liệu sau
                khi api.boCauBinhLuan() trả về. Nếu kiểm `length === 0` trước, màn
                sẽ khẳng định "Chưa có bộ câu nào" trong suốt lúc chờ — rồi danh
                sách lại hiện ra. Người dùng vừa được báo là trống xong lại thấy có.
                Tệ hơn: lời nhắc "Soạn vài câu rồi bấm Lưu" xui họ tạo lại thứ đã có.
                Nên phải hỏi `dangTai` TRƯỚC, empty state chỉ nói khi đã biết chắc. */}
            {dangTai ? (
              <DangTai dong={2} />
            ) : boCau.length === 0 ? (
              <ChuaCoDuLieu chu="Chưa có bộ câu nào." goiY="Soạn vài câu rồi bấm Lưu bộ câu để dùng lại." />
            ) : (
              <div className="divide-y divide-slate-50 dark:divide-slate-700/50">
                {boCau.map(b => (
                  <button
                    key={b.id}
                    onClick={() => setCau((b.items || []).length ? b.items : [''])}
                    className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700/30"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold text-slate-800 dark:text-slate-100">{b.name}</span>
                      <span className="text-xs text-slate-400">{b.count} câu · cập nhật {gioNgan(b.updatedAt)}</span>
                    </span>
                    <span className="shrink-0 text-xs font-semibold text-brand-600 dark:text-brand-400">Dùng</span>
                  </button>
                ))}
              </div>
            )}
          </The>

          <The className="p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-slate-300">
                  Giãn cách mỗi bình luận (giây)
                </label>
                <div className="flex items-center gap-2">
                  <input type="number" min={neSpam ? GIAN_CACH_NE_SPAM_TOI_THIEU : GIAN_CACH_TOI_THIEU} max={GIAN_CACH_TOI_DA} value={tuGiay} onChange={e => setTuGiay(Number(e.target.value) || 0)} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
                  <span className="shrink-0 text-xs text-slate-400">đến</span>
                  <input type="number" min={neSpam ? GIAN_CACH_NE_SPAM_TOI_THIEU : GIAN_CACH_TOI_THIEU} max={GIAN_CACH_TOI_DA} value={denGiay} onChange={e => setDenGiay(Number(e.target.value) || 0)} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
                </div>
              </div>
              <div className="flex items-end pb-2">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                  <input type="checkbox" checked={neSpam} onChange={e => setNeSpam(e.target.checked)} className="h-4 w-4 rounded border-slate-300" />
                  <ShieldCheck className="h-4 w-4 text-slate-400" />
                  Né spam
                </label>
              </div>
            </div>
          </The>
        </div>

        <The>
          <DauThe
            icon={<Search className="h-4 w-4" />}
            tieuDe="Bài đã đăng"
            dem={daChon.size + '/' + bai.length}
            phai={<NutPhu onClick={tai}><RefreshCcw className="h-3.5 w-3.5" />Làm mới</NutPhu>}
          />
          <div className="space-y-3 p-5">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={tim}
                onChange={e => setTim(e.target.value)}
                placeholder="Tìm theo nội dung, ID bài hoặc ID nhóm..."
                className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-brand-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              />
            </div>

            <div className="max-h-[30rem] overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700">
              {dangTai ? (
                <DangTai dong={4} />
              ) : baiLoc.length === 0 ? (
                <ChuaCoDuLieu chu={bai.length ? 'Không tìm thấy bài phù hợp.' : 'Chưa có bài đăng thành công nào.'} goiY="Bài đăng thành công có link bằng chứng sẽ hiện ở đây." />
              ) : (
                baiLoc.map(b => {
                  const id = String(b.postId);
                  const chon = daChon.has(id);
                  return (
                    <button
                      key={id}
                      onClick={() => setDaChon(s => {
                        const m = new Set(s);
                        if (m.has(id)) m.delete(id); else m.add(id);
                        return m;
                      })}
                      className={
                        'flex w-full items-start gap-3 border-b border-slate-50 px-3 py-3 text-left last:border-0 dark:border-slate-700/50 ' +
                        (chon ? 'bg-brand-50 dark:bg-brand-600/10' : 'hover:bg-slate-50 dark:hover:bg-slate-700/30')
                      }
                    >
                      <span className={'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ' + (chon ? 'border-brand-600 bg-brand-600 text-white' : 'border-slate-300 dark:border-slate-600')}>
                        {chon && <CircleCheck className="h-3 w-3" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-slate-800 dark:text-slate-100">{b.message || '(không có nội dung)'}</span>
                        <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-400">
                          <span>Post {id}</span>
                          {b.groupId && <span>Nhóm {b.groupId}</span>}
                          <span>{gioNgan(b.createdAt)}</span>
                          {b.url && <a href={b.url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} className="inline-flex items-center gap-1 text-brand-600 hover:underline dark:text-brand-400">Mở <ExternalLink className="h-3 w-3" /></a>}
                        </span>
                      </span>
                    </button>
                  );
                })
              )}
            </div>

            <div className="grid gap-2 border-t border-slate-100 pt-4 dark:border-slate-700 sm:grid-cols-[minmax(0,1fr)_auto]">
              <input
                value={nhapTay}
                onChange={e => setNhapTay(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); themTargetThuCong(); } }}
                placeholder="Hoặc dán link/ID bài viết ngoài công cụ"
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              />
              <NutPhu onClick={themTargetThuCong} disabled={!nhapTay.trim()}>
                <Plus className="h-3.5 w-3.5" />
                Thêm bài
              </NutPhu>
            </div>

            {postThuCong.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {postThuCong.map(id => (
                  <span key={id} className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 text-xs text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                    {id}
                    <button onClick={() => setDaChon(s => { const m = new Set(s); m.delete(id); return m; })} aria-label={`Bỏ ${id}`} className="text-slate-400 hover:text-rose-600">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/30 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {loiHopLe
                  ? <span className="inline-flex items-center gap-1.5 text-amber-600 dark:text-amber-400"><CircleAlert className="h-3.5 w-3.5" />{loiHopLe}</span>
                  : <>Sẽ tạo <span className="font-semibold text-slate-700 dark:text-slate-200">{tongJob}</span> job bình luận ({cauHopLe.length} câu × {daChon.size} bài).</>}
              </p>
              <NutChinh onClick={gui} disabled={Boolean(loiHopLe) || dangGui || !actorUid}>
                <Send className="h-4 w-4" />
                {dangGui ? 'Đang đưa vào hàng đợi...' : 'Bắt đầu bình luận'}
              </NutChinh>
            </div>
          </div>
        </The>
      </div>
    </div>
  );
}
